import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, Trash2, X, Gift, Edit2, Users, UserCheck, UserX, ChevronDown, ChevronUp, Clock, Image as ImageIcon, Trophy, TrendingUp, Loader2, Calendar } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, pickLocalizedText } from '../../utils/localizedContent';


const TOURNAMENT_TYPES = [
  { key: 'groups_elimination', label_es: 'Fase de grupos + Eliminatoria', label_en: 'Group Stage + Elimination' },
  { key: 'league', label_es: 'Liga (todos contra todos)', label_en: 'League (round-robin)' },
  { key: 'swiss', label_es: 'Sistema suizo (balanceado)', label_en: 'Swiss System (balanced)' },
];

const DEFAULT_FORM = {
  title: '', title_es: '', description: '', description_es: '',
  event_type: 'tournament',
  tournament_type: 'swiss',
  tcg_type: 'pokemon', date: '', time: '18:00',
  max_participants: 32, entry_fee: 0, prize: '', image: '',
  allow_registration: true,
  checkin_opens_hours_before: '',
  custom_prizes: [],
  prize_cash: 0,
  prize_products: [],
  format_config: {
    group_size: 4,
    qualifiers_per_group: 2,
    league_mode: 'full_round_robin',
  },
};

const LEVEL_BADGE = {
  bronze:   'text-amber-400 bg-amber-700/20',
  silver:   'text-gray-300 bg-gray-400/20',
  gold:     'text-[#C9A962] bg-[#C9A962]/20',
  platinum: 'text-cyan-300 bg-cyan-400/20',
};

function getLevel(pts) {
  if (pts >= 10000) return 'platinum';
  if (pts >= 2500) return 'gold';
  if (pts >= 500) return 'silver';
  return 'bronze';
}

function getPrizeSummary(event, lang) {
  const cash = Number(event?.prize_cash || 0);
  const products = Array.isArray(event?.prize_products) ? event.prize_products : [];
  const totalItems = products.reduce((acc, product) => acc + Number(product.quantity || 1), 0);
  const productNames = products
    .slice(0, 2)
    .map((product) => product?.name || product?.product_id)
    .filter(Boolean);

  if (cash <= 0 && products.length === 0) return null;

  return {
    cashLabel: cash > 0 ? `${cash.toFixed(2)}€` : null,
    productsLabel: products.length > 0
      ? pickLocalizedText(
        lang,
        `${totalItems} producto${totalItems === 1 ? '' : 's'}`,
        `${totalItems} product${totalItems === 1 ? '' : 's'}`,
      )
      : null,
    previewLabel: productNames.join(' · '),
  };
}

export default function AdminEvents({ events, onRefresh, onDelete, lang, setMessage }) {
  const [adminTab, setAdminTab] = useState('tournaments'); // 'tournaments' | 'events' | 'users'
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [expandedParticipants, setExpandedParticipants] = useState(null);
  const [participants, setParticipants] = useState({});
  const [loadingParticipants, setLoadingParticipants] = useState(null);
  const [userScores, setUserScores] = useState([]);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productFilter, setProductFilter] = useState({ q: '', category: '', tcg: '' });
  const imageFileRef = useRef(null);

  const l = createTextPicker(lang);
  const isTournament = form.event_type === 'tournament';
  const tournamentHasLeaguePhase = isTournament && ['league', 'groups_elimination'].includes(form.tournament_type);
  const prizeProductsTotal = ((form.prize_products || []).reduce(
    (acc, p) => acc + Number(p.price || 0) * Number(p.quantity || 1),
    0,
  )).toFixed(2);
  const openNativeDatePicker = (event) => {
    const input = event.currentTarget;
    if (typeof input?.showPicker === 'function') input.showPicker();
  };

  const fetchUserScores = useCallback(async () => {
    setScoresLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/events/user-scores`);
      setUserScores(data.leaderboard || []);
    } catch { setUserScores([]); }
    setScoresLoading(false);
  }, []);

  useEffect(() => {
    if (adminTab === 'users') fetchUserScores();
  }, [adminTab, fetchUserScores]);

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showForm && (isTournament || form.event_type === 'event')) fetchProducts();
  }, [showForm, isTournament, fetchProducts]);

  const resetForm = useCallback((targetType = 'tournament') => {
    setForm({ ...DEFAULT_FORM, event_type: targetType });
    setEditingId(null);
  }, []);

  useEffect(() => {
    if (adminTab === 'users') {
      setShowForm(false);
      return;
    }
    // Mantener formularios acotados a la pestaña de gestión seleccionada.
    resetForm(adminTab === 'events' ? 'event' : 'tournament');
    setShowForm(false);
  }, [adminTab, resetForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedPrizeCash = parseFloat(form.prize_cash || 0);
    const normalizedPrizeProducts = form.prize_products || [];
    const data = {
      ...form,
      entry_fee: parseFloat(form.entry_fee || 0),
      max_participants: parseInt(form.max_participants),
      checkin_opens_hours_before: form.checkin_opens_hours_before ? parseInt(form.checkin_opens_hours_before) : null,
      tournament_type: isTournament ? (form.tournament_type || 'swiss') : null,
      allow_registration: isTournament ? true : form.allow_registration,
      custom_prizes: isTournament ? [] : [],
      prize: '',
      prize_es: '',
      prize_cash: normalizedPrizeCash,
      prize_products: normalizedPrizeProducts,
      format_config: isTournament ? (form.format_config || {}) : {},
    };
    try {
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, data);
        setMessage(l('Evento actualizado', 'Event updated'));
      } else {
        await axios.post(`${API}/events`, data);
        setMessage(l('Evento creado', 'Event created'));
      }
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const openCreateForm = () => {
    const targetType = adminTab === 'events' ? 'event' : 'tournament';
    resetForm(targetType);
    setShowForm(true);
  };

  const handleEdit = (event) => {
    const hasTournamentMarkers = Boolean(
      event?.tournament_type ||
      (Array.isArray(event?.custom_prizes) && event.custom_prizes.length > 0) ||
      Number(event?.prize_cash || 0) > 0 ||
      (Array.isArray(event?.prize_products) && event.prize_products.length > 0)
    );
    const normalizedEventType = event?.event_type === 'tournament' || hasTournamentMarkers
      ? 'tournament'
      : 'event';

    setForm({
      title: event.title || '', title_es: event.title_es || '',
      description: event.description || '', description_es: event.description_es || '',
      event_type: normalizedEventType,
      tournament_type: event.tournament_type || 'swiss',
      tcg_type: event.tcg_type || 'pokemon',
      date: event.date || '', time: event.time || '18:00',
      max_participants: event.max_participants || 32,
      entry_fee: event.entry_fee || 0,
      prize: event.prize || '', image: event.image || '',
      allow_registration: event.allow_registration !== false,
      checkin_opens_hours_before: event.checkin_opens_hours_before ?? '',
      custom_prizes: event.custom_prizes?.length ? event.custom_prizes : [],
      prize_cash: event.prize_cash || 0,
      prize_products: event.prize_products || [],
      format_config: {
        group_size: event.format_config?.group_size || 4,
        qualifiers_per_group: event.format_config?.qualifiers_per_group || 2,
        league_mode: event.format_config?.league_mode || 'full_round_robin',
      },
    });
    setEditingId(event.event_id);
    setShowForm(true);
  };

  const visibleEvents = events.filter((ev) => {
    if (adminTab === 'events') return ev.event_type === 'event';
    if (adminTab === 'tournaments') return ev.event_type === 'tournament';
    return true;
  });
  const isEventTab = adminTab === 'events';
  const isTournamentTab = adminTab === 'tournaments';

  const addPrizeProduct = (product) => {
    setForm((f) => {
      const exists = (f.prize_products || []).some((p) => p.product_id === product.product_id);
      if (exists) return f;
      return {
        ...f,
        prize_products: [
          ...(f.prize_products || []),
          { product_id: product.product_id, name: product.name, price: Number(product.price || 0), quantity: 1 },
        ],
      };
    });
  };
  const removePrizeProduct = (productId) => setForm((f) => ({ ...f, prize_products: (f.prize_products || []).filter((p) => p.product_id !== productId) }));
  const updatePrizeProductQty = (productId, qty) => setForm((f) => ({
    ...f,
    prize_products: (f.prize_products || []).map((p) => (p.product_id === productId ? { ...p, quantity: Math.max(1, parseInt(qty || 1, 10)) } : p)),
  }));

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, image: ev.target.result }));
    reader.readAsDataURL(file);
  };

  // ---- Gestión de participantes ----
  const toggleParticipants = async (eventId) => {
    if (expandedParticipants === eventId) { setExpandedParticipants(null); return; }
    setExpandedParticipants(eventId);
    setLoadingParticipants(eventId);
    try {
      const { data } = await axios.get(`${API}/events/${eventId}/participants`);
      setParticipants(prev => ({ ...prev, [eventId]: data.participants || [] }));
    } catch (err) { setMessage(formatApiError(err)); }
    setLoadingParticipants(null);
  };

  const handleCheckIn = async (eventId, userId) => {
    try {
      await axios.post(`${API}/events/${eventId}/admin/check-in/${userId}`);
      setParticipants(prev => ({ ...prev, [eventId]: (prev[eventId] || []).map(p => p.user_id === userId ? { ...p, is_checked_in: true } : p) }));
      setMessage(l('Check-in realizado', 'Checked in'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const handleRemoveParticipant = async (eventId, userId) => {
    if (!window.confirm(l('¿Eliminar este participante del evento?', 'Remove this participant from the event?'))) return;
    try {
      await axios.delete(`${API}/events/${eventId}/participants/${userId}`);
      setParticipants(prev => ({ ...prev, [eventId]: (prev[eventId] || []).filter(p => p.user_id !== userId) }));
      setMessage(l('Participante eliminado', 'Participant removed'));
      onRefresh();
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const inputClass = "w-full bg-background border border-border rounded-xl px-4 py-2.5 text-foreground text-sm outline-none focus:border-primary transition-colors";

  return (
    <div>
      {/* Admin tabs */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2">
          <button onClick={() => setAdminTab('tournaments')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${adminTab === 'tournaments' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}>
            <Trophy size={14} />{l('Torneos', 'Tournaments')}
          </button>
          <button onClick={() => setAdminTab('events')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${adminTab === 'events' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}>
            <Calendar size={14} />{l('Eventos', 'Events')}
          </button>
          <button onClick={() => setAdminTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${adminTab === 'users' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}>
            <TrendingUp size={14} />{l('Usuarios', 'Users')}
          </button>
        </div>
        {(isTournamentTab || isEventTab) && (
          <button onClick={openCreateForm} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
            <Plus size={16} />
            {isTournamentTab ? l('Nuevo Torneo', 'New Tournament') : l('Nuevo Evento', 'New Event')}
          </button>
        )}
      </div>

      {/* ─── USERS TAB ─── */}
      {adminTab === 'users' && (
        <div>
          {scoresLoading ? (
            <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-primary" /></div>
          ) : userScores.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 text-sm">{l('No hay datos de torneos completados aún.', 'No completed tournament data yet.')}</p>
          ) : (
            <div className="space-y-2">
              {userScores.map((player, idx) => {
                const level = getLevel(player.points || 0);
                return (
                  <div key={player.user_id} className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-4">
                    <span className="text-muted-foreground text-sm font-bold w-6 text-center">#{idx + 1}</span>
                    <div className="w-8 h-8 rounded-full bg-[#C9A962]/20 flex items-center justify-center text-[#C9A962] font-bold text-sm">
                      {(player.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-semibold text-sm truncate">{player.name}</p>
                      <p className="text-muted-foreground text-xs">{player.wins}W · {player.losses}L · {player.championships} 🏆 · {player.tournaments_played} torneos</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#C9A962] font-bold text-sm">{(player.points || 0).toLocaleString()} pts</p>
                      <p className="text-muted-foreground text-xs">{Math.round((player.win_rate || 0) * 100)}% win</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${LEVEL_BADGE[level]}`}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── TOURNAMENTS / EVENTS TABS ─── */}
      {(isTournamentTab || isEventTab) && (
        <>
          {/* Event Form */}
          {showForm && (
            <div className="bg-card border border-border rounded-2xl p-6 mb-6">
              <div className="flex justify-between mb-5">
                <h3 className="text-foreground font-bold">
                  {editingId
                    ? (isTournament ? l('Editar Torneo', 'Edit Tournament') : l('Editar Evento', 'Edit Event'))
                    : (isTournament ? l('Nuevo Torneo', 'New Tournament') : l('Nuevo Evento', 'New Event'))}
                </h3>
                <button onClick={() => { setShowForm(false); resetForm(isTournamentTab ? 'tournament' : 'event'); }}><X size={18} className="text-muted-foreground" /></button>
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Título (EN)', 'Title (EN)')} *</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Championship 2025" className={inputClass} /></div>

                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Título (ES)', 'Title (ES)')}</label>
                  <input value={form.title_es} onChange={e => setForm(f => ({ ...f, title_es: e.target.value }))} placeholder="Campeonato 2025" className={inputClass} /></div>

                {/* Event type fixed by active tab to avoid cross-type residuals */}
                <input type="hidden" value={form.event_type} />

                {/* Tournament type — only for tournaments */}
                {isTournament ? (
                  <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Tipo de Torneo', 'Tournament Type')}</label>
                    <select value={form.tournament_type} onChange={e => setForm(f => ({ ...f, tournament_type: e.target.value }))} className={inputClass}>
                      {TOURNAMENT_TYPES.map(tt => (
                        <option key={tt.key} value={tt.key}>{pickLocalizedText(lang, tt.label_es, tt.label_en)}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  /* Allow registration — only for events */
                  <div className="flex items-center gap-3 pt-5">
                    <div
                      className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${form.allow_registration ? 'bg-[#C9A962]' : 'bg-[#A8B5AE]/30'}`}
                      onClick={() => setForm(f => ({ ...f, allow_registration: !f.allow_registration }))}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${form.allow_registration ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                    <span className="text-muted-foreground text-sm">{l('Permitir inscripciones', 'Allow registrations')}</span>
                  </div>
                )}

                {tournamentHasLeaguePhase && (
                  <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-background border border-border rounded-xl p-3">
                    <div>
                      <label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Modo liga', 'League mode')}</label>
                      <select
                        value={form.format_config?.league_mode || 'full_round_robin'}
                        onChange={e => setForm(f => ({ ...f, format_config: { ...(f.format_config || {}), league_mode: e.target.value } }))}
                        className={inputClass}
                      >
                        <option value="full_round_robin">{l('Todos contra todos', 'Full round-robin')}</option>
                        <option value="partial_round_robin">{l('Parcial', 'Partial')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Tamaño de grupo', 'Group size')}</label>
                      <input
                        type="number"
                        min="3"
                        max="8"
                        value={form.format_config?.group_size || 4}
                        onChange={e => setForm(f => ({ ...f, format_config: { ...(f.format_config || {}), group_size: parseInt(e.target.value || 4, 10) } }))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Clasificados por grupo', 'Qualifiers per group')}</label>
                      <input
                        type="number"
                        min="1"
                        max="4"
                        value={form.format_config?.qualifiers_per_group || 2}
                        onChange={e => setForm(f => ({ ...f, format_config: { ...(f.format_config || {}), qualifiers_per_group: parseInt(e.target.value || 2, 10) } }))}
                        className={inputClass}
                      />
                    </div>
                  </div>
                )}

                {/* TCG */}
                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Juego / TCG', 'Game / TCG')} *</label>
                  <select value={form.tcg_type} onChange={e => setForm(f => ({ ...f, tcg_type: e.target.value }))} className={inputClass}>
                    <option value="pokemon">Pokémon</option>
                    <option value="magic">Magic</option>
                    <option value="yugioh">Yu-Gi-Oh!</option>
                    <option value="onepiece">One Piece</option>
                    <option value="board_game">{l('Juego de Mesa', 'Board Game')}</option>
                    <option value="all">{l('Todos', 'All')}</option>
                  </select>
                </div>

                {/* Date / Time */}
                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Fecha', 'Date')} *</label>
                  <input
                    value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    onClick={openNativeDatePicker}
                    onFocus={openNativeDatePicker}
                    required
                    type="date"
                    className={inputClass}
                  /></div>

                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Hora', 'Time')} *</label>
                  <input value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} required type="time" className={inputClass} /></div>

                {/* Max participants — always for tournament, only if allow_registration for events */}
                {(isTournament || form.allow_registration) && (
                  <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Máx. participantes', 'Max participants')} *</label>
                    <input value={form.max_participants} onChange={e => setForm(f => ({ ...f, max_participants: e.target.value }))} type="number" className={inputClass} /></div>
                )}

                {/* Entry fee */}
                <div><label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Cuota (€)', 'Entry fee (€)')}</label>
                  <input value={form.entry_fee} onChange={e => setForm(f => ({ ...f, entry_fee: e.target.value }))} type="number" step="0.01" placeholder="0 = gratis" className={inputClass} /></div>

                {/* Image — URL + file upload */}
                <div className="sm:col-span-2">
                  <label className="text-muted-foreground text-xs font-medium mb-1 block flex items-center gap-1.5"><ImageIcon size={12} />{l('Imagen del evento', 'Event image')}</label>
                  <div className="flex gap-2">
                    <input value={form.image && !form.image.startsWith('data:') ? form.image : ''}
                      onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                      placeholder="https://..." className={`flex-1 ${inputClass}`} />
                    <button type="button" onClick={() => imageFileRef.current?.click()}
                      className="px-3 py-2 bg-card border border-border rounded-xl text-muted-foreground hover:text-foreground text-sm whitespace-nowrap flex items-center gap-1.5">
                      <ImageIcon size={13} />{l('Subir', 'Upload')}
                    </button>
                    <input ref={imageFileRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
                  </div>
                  {form.image && (
                    <img src={form.image} alt="preview" className="mt-2 h-16 rounded-lg object-cover border border-border" />
                  )}
                </div>

                {/* Descriptions */}
                <div className="sm:col-span-2">
                  <label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Descripción (EN)', 'Description (EN)')} *</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required rows={2} className={`${inputClass} resize-none`} /></div>

                <div className="sm:col-span-2">
                  <label className="text-muted-foreground text-xs font-medium mb-1 block">{l('Descripción (ES)', 'Description (ES)')}</label>
                  <textarea value={form.description_es} onChange={e => setForm(f => ({ ...f, description_es: e.target.value }))} rows={2} className={`${inputClass} resize-none`} /></div>

                {/* Check-in window */}
                <div className="sm:col-span-2">
                  <label className="text-muted-foreground text-xs font-medium mb-1 block flex items-center gap-1"><Clock size={11} />{l('Abrir check-in X horas antes (opcional)', 'Open check-in X hours before (optional)')}</label>
                  <input value={form.checkin_opens_hours_before} onChange={e => setForm(f => ({ ...f, checkin_opens_hours_before: e.target.value }))} type="number" min="0" placeholder="2" className="w-36 bg-background border border-border rounded-xl px-4 py-2.5 text-foreground text-sm outline-none focus:border-primary" />
                </div>

                <div className="sm:col-span-2 space-y-3">
                  <div>
                    <label className="text-muted-foreground text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-2">
                      <Gift size={12} />{isTournament ? l('Premios del torneo', 'Tournament prizes') : l('Premios del evento', 'Event prizes')}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-background border border-border rounded-xl p-3">
                        <label className="text-muted-foreground text-[10px] uppercase block mb-1">{l('Dinero (€)', 'Cash (€)')}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.prize_cash}
                          onChange={(e) => setForm((f) => ({ ...f, prize_cash: e.target.value }))}
                          className="w-full bg-card border border-border text-foreground rounded-lg px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="bg-background border border-border rounded-xl p-3">
                        <label className="text-muted-foreground text-[10px] uppercase block mb-1">{l('Productos seleccionados', 'Selected products')}</label>
                        <p className="text-muted-foreground text-xs">
                          {(form.prize_products || []).length} {l('producto(s) en premio', 'product(s) in prizes')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-3 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        value={productFilter.q}
                        onChange={(e) => setProductFilter((f) => ({ ...f, q: e.target.value }))}
                        placeholder={l('Buscar producto...', 'Search product...')}
                        className="bg-card border border-border text-foreground rounded-lg px-2 py-1.5 text-sm"
                      />
                      <input
                        value={productFilter.category}
                        onChange={(e) => setProductFilter((f) => ({ ...f, category: e.target.value }))}
                        placeholder={l('Filtrar categoría', 'Filter category')}
                        className="bg-card border border-border text-foreground rounded-lg px-2 py-1.5 text-sm"
                      />
                      <input
                        value={productFilter.tcg}
                        onChange={(e) => setProductFilter((f) => ({ ...f, tcg: e.target.value }))}
                        placeholder={l('Filtrar TCG', 'Filter TCG')}
                        className="bg-card border border-border text-foreground rounded-lg px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {productsLoading ? (
                        <p className="text-muted-foreground text-xs">{l('Cargando productos...', 'Loading products...')}</p>
                      ) : (
                        products
                          .filter((p) => {
                            const q = productFilter.q.trim().toLowerCase();
                            const cat = productFilter.category.trim().toLowerCase();
                            const tcg = productFilter.tcg.trim().toLowerCase();
                            const name = (p.name || '').toLowerCase();
                            const pcat = (p.category || '').toLowerCase();
                            const ptcg = (p.tcg_type || '').toLowerCase();
                            return (!q || name.includes(q)) && (!cat || pcat.includes(cat)) && (!tcg || ptcg.includes(tcg));
                          })
                          .slice(0, 80)
                          .map((p) => (
                            <button
                              key={p.product_id}
                              type="button"
                              onClick={() => addPrizeProduct(p)}
                              className="w-full text-left px-2 py-1.5 rounded-lg bg-card border border-border hover:border-primary/40"
                            >
                              <span className="text-foreground text-xs">{p.name}</span>
                              <span className="text-muted-foreground text-[11px] ml-2">({Number(p.price || 0).toFixed(2)}€)</span>
                            </button>
                          ))
                      )}
                    </div>
                    <div className="space-y-2">
                      {(form.prize_products || []).map((p) => (
                        <div key={p.product_id} className="flex items-center gap-2 bg-card border border-border rounded-lg px-2 py-1.5">
                          <span className="text-foreground text-xs flex-1 truncate">{p.name || p.product_id}</span>
                          <span className="text-muted-foreground text-xs">{Number(p.price || 0).toFixed(2)}€</span>
                          <input
                            type="number"
                            min="1"
                            value={p.quantity || 1}
                            onChange={(e) => updatePrizeProductQty(p.product_id, e.target.value)}
                            className="w-16 bg-background border border-border text-foreground rounded px-1.5 py-1 text-xs"
                          />
                          <button type="button" onClick={() => removePrizeProduct(p.product_id)} className="p-1 text-[#EF4444] hover:bg-red-500/10 rounded">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {(form.prize_products || []).length === 0 && (
                        <p className="text-muted-foreground text-xs">{l('Sin productos añadidos.', 'No products added.')}</p>
                      )}
                      <div className="text-right text-xs text-[#C9A962]">
                        {l('Total productos', 'Products total')}: {prizeProductsTotal}€
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" className="sm:col-span-2 btn-primary py-2.5 text-sm">
                  {editingId ? l('Guardar Cambios', 'Save Changes') : l('Crear Evento', 'Create Event')}
                </button>
              </form>
            </div>
          )}

          {/* Events List */}
          <div className="space-y-3">
            {visibleEvents.length === 0 && (
              <div className="bg-card border border-border rounded-2xl p-8 text-center">
                <Trophy size={44} className="mx-auto mb-3 text-primary/40" />
                <h4 className="text-foreground font-semibold mb-1">
                  {isTournamentTab ? l('No hay torneos todavía', 'No tournaments yet') : l('No hay eventos todavía', 'No events yet')}
                </h4>
                <p className="text-muted-foreground text-sm mb-5">
                  {isTournamentTab
                    ? l('Crea el primer torneo para que aparezca aquí.', 'Create your first tournament to see it here.')
                    : l('Crea el primer evento para que aparezca aquí.', 'Create your first event to see it here.')}
                </p>
                <button onClick={openCreateForm} className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2">
                  <Plus size={16} />
                  {isTournamentTab ? l('Nuevo Torneo', 'New Tournament') : l('Nuevo Evento', 'New Event')}
                </button>
              </div>
            )}
            {visibleEvents.map(ev => {
              const prizeSummary = getPrizeSummary(ev, lang);
              return (
              <div key={ev.event_id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-foreground font-semibold truncate">{ev.title}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ev.event_type === 'tournament' ? 'bg-[#C9A962]/20 text-[#C9A962]' : 'bg-[#3B82F6]/20 text-[#3B82F6]'}`}>
                        {ev.event_type === 'tournament' ? l('Torneo', 'Tournament') : l('Evento', 'Event')}
                      </span>
                      {ev.tournament_type && (
                        <span className="text-muted-foreground text-[10px]">
                          {(() => {
                            const foundType = TOURNAMENT_TYPES.find(t => t.key === ev.tournament_type);
                            return foundType ? pickLocalizedText(lang, foundType.label_es, foundType.label_en) : ev.tournament_type;
                          })()}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">{ev.date} · {ev.time} · {ev.registered_participants?.length || 0}/{ev.max_participants}</p>
                    {prizeSummary && (
                      <div className="mt-2 inline-flex max-w-full items-start gap-2 rounded-lg border border-[#C9A962]/20 bg-[#C9A962]/10 px-2.5 py-1.5">
                        <Gift size={12} className="mt-0.5 shrink-0 text-[#C9A962]" />
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-[#E7D29A]">
                            {l('Premio', 'Prize')}
                            {prizeSummary.cashLabel && ` · ${prizeSummary.cashLabel}`}
                            {prizeSummary.productsLabel && ` · ${prizeSummary.productsLabel}`}
                          </p>
                          {prizeSummary.previewLabel && (
                            <p className="truncate text-[11px] text-[#D3C6A0]">{prizeSummary.previewLabel}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ev.entry_fee === 0 ? 'bg-[#10B981]/20 text-[#34D399]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'}`}>
                    {ev.entry_fee === 0 ? l('Gratis', 'Free') : `${ev.entry_fee?.toFixed(2)}€`}
                  </span>
                  {(ev.registered_participants?.length || 0) > 0 && (
                    <button onClick={() => toggleParticipants(ev.event_id)}
                      className={`flex items-center gap-1 p-1.5 rounded-lg text-xs transition-colors ${expandedParticipants === ev.event_id ? 'bg-blue-500/20 text-blue-500' : 'bg-muted/50 text-muted-foreground hover:bg-blue-500/10 hover:text-blue-500'}`}>
                      <Users size={13} /><span>{ev.registered_participants?.length || 0}</span>
                      {expandedParticipants === ev.event_id ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    </button>
                  )}
                  <button onClick={() => handleEdit(ev)} className="p-1.5 rounded-lg text-[#C9A962] hover:bg-[#C9A962]/10"><Edit2 size={14} /></button>
                  <button onClick={() => onDelete(ev.event_id)} className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10"><Trash2 size={14} /></button>
                </div>

                {expandedParticipants === ev.event_id && (
                  <div className="border-t border-border px-4 py-3 bg-background/40">
                    {loadingParticipants === ev.event_id ? (
                      <p className="text-muted-foreground text-sm">{l('Cargando...', 'Loading...')}</p>
                    ) : (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {(participants[ev.event_id] || []).map(p => (
                          <div key={p.user_id} className="flex items-center gap-3 bg-card rounded-lg px-3 py-2">
                            <div className="w-6 h-6 rounded-full bg-[#C9A962]/20 flex items-center justify-center text-[#C9A962] text-xs font-bold">
                              {(p.name || '?')[0].toUpperCase()}
                            </div>
                            <span className="text-foreground text-sm flex-1 truncate">{p.name || p.email}</span>
                            {p.is_checked_in ? (
                              <span className="flex items-center gap-1 text-[#10B981] text-xs"><UserCheck size={12} /> Check-in</span>
                            ) : (
                              <button onClick={() => handleCheckIn(ev.event_id, p.user_id)}
                                className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/15 text-[#10B981] rounded text-xs hover:bg-[#10B981]/25">
                                <UserCheck size={11} /> Check-in
                              </button>
                            )}
                            <button onClick={() => handleRemoveParticipant(ev.event_id, p.user_id)}
                              className="p-1 rounded text-[#EF4444] hover:bg-red-500/10">
                              <UserX size={13} />
                            </button>
                          </div>
                        ))}
                        {(participants[ev.event_id] || []).length === 0 && (
                          <p className="text-muted-foreground text-sm">{l('Sin participantes', 'No participants')}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );})}
          </div>
        </>
      )}
    </div>
  );
}
